/**
 * The Time Travel entity catalog.
 *
 * ── WHY A HAND-WRITTEN CATALOG AND NOT INTROSPECTION ─────────────────────────
 *
 * The obvious implementation reads foreign keys from `information_schema` and
 * derives the write order. It does not work here, because this schema is
 * FK-MIXED — verified, not assumed:
 *
 *   ProductVariant.productId    -> real FK, onDelete: Cascade  (schema.prisma)
 *   CollectionProduct.productId -> bare column + index, NO FK
 *   OrderItem.productId         -> bare column + index, NO FK
 *
 * The newer transactional models reference by id and validate in the service
 * layer, which is a deliberate choice in this codebase. For an undo engine it
 * has three consequences, and all three are load-bearing:
 *
 *   1. Order cannot be derived. For the bare-column majority there is nothing
 *      in information_schema to read. The graph has to be declared, which makes
 *      it code that can drift from the schema — hence the CI test below.
 *
 *   2. `SET CONSTRAINTS ALL DEFERRED` protects only the declared minority. It
 *      is not the safety net; the ordering is.
 *
 *   3. The same undo cascade-deletes on one side of an aggregate and leaves
 *      silent orphans on the other. Deleting a Product removes its variants
 *      automatically (rows the preview never showed the merchant) and orphans
 *      its collection_products (no error at all). Expansion must therefore
 *      follow declared cascades AS WELL AS catalogued children, and the preview
 *      must show both — a preview that understates the blast radius is worse
 *      than no preview, because the merchant approved it.
 *
 * Since the database will not catch our mistakes, the pre-COMMIT invariant
 * check in TimeTravelService is the only safety net that exists.
 *
 * ── FAIL CLOSED ──────────────────────────────────────────────────────────────
 *
 * A table not in this catalog and not in NOT_TIME_TRAVELLABLE cannot be
 * time-travelled, and the CI test fails on the PR that adds it. Missing a child
 * table produces a corrupt aggregate that nothing can detect afterwards, so the
 * cost of a false refusal is a support ticket and the cost of a false
 * acceptance is unrecoverable data.
 */

export interface TimeTravelChild {
  /** Physical table name (the @@map value). */
  table: string;
  /** Column on `table` pointing at the parent. */
  fk: string;
  /** Join path when this is not a direct child of the root. */
  via?: string;
  /** FK topological depth. 0 is the root; children must be > their parent. */
  depth: number;
  /** Is there a real database FK? Drives whether DEFERRED can help. */
  enforced: boolean;
  /** Will a parent DELETE remove this row automatically? */
  cascades?: boolean;
}

export interface TimeTravelEntity {
  /** Prisma model name, used as `entityType`. */
  model: string;
  rootTable: string;
  children: TimeTravelChild[];
  /**
   * Columns elsewhere that make a row untouchable. If any of these reference
   * the row, it goes to `protectedSkips` rather than being reverted.
   */
  protectedBy: string[];
  /** Self-referential parent column, if any. Requires cycle detection. */
  selfReference?: string;
  /** Shown to the merchant when the whole aggregate is refused. */
  refuseReason?: string;
}

export const TIME_TRAVEL_ENTITIES: Record<string, TimeTravelEntity> = {
  Product: {
    model: 'Product',
    rootTable: 'products',
    children: [
      { table: 'product_variants', fk: 'productId', depth: 1, enforced: true, cascades: true },
      { table: 'product_images', fk: 'productId', depth: 1, enforced: true, cascades: true },
      { table: 'inventory', fk: 'productId', depth: 1, enforced: true, cascades: true },
      { table: 'wishlist_items', fk: 'productId', depth: 1, enforced: true, cascades: true },
      // Bare columns — no FK, no cascade, orphans on delete.
      { table: 'product_option_values', fk: 'productId', depth: 1, enforced: false },
      { table: 'collection_products', fk: 'productId', depth: 1, enforced: false },
      { table: 'inventory_items', fk: 'variantId', via: 'product_variants', depth: 2, enforced: false },
      { table: 'inventory_levels', fk: 'inventoryItemId', via: 'inventory_items', depth: 3, enforced: false },
    ],
    // Never revert a product out from under a sold order or a live checkout.
    protectedBy: ['order_items.productId', 'checkout_line_items.productId'],
  },

  Collection: {
    model: 'Collection',
    rootTable: 'collections',
    children: [{ table: 'collection_products', fk: 'collectionId', depth: 1, enforced: false }],
    protectedBy: [],
  },

  Category: {
    model: 'Category',
    rootTable: 'categories',
    children: [],
    // Self-referential (Category.parentId -> categories, onDelete: Restrict).
    // Depth-based ordering does not terminate on a cycle, and a reverted
    // re-parenting can create one, so expansion must do a real topological sort
    // with cycle detection and refuse rather than loop or truncate.
    selfReference: 'parentId',
    protectedBy: ['products.categoryId'],
  },

  Customer: {
    model: 'Customer',
    rootTable: 'customers',
    children: [
      { table: 'customer_addresses', fk: 'customerId', depth: 1, enforced: false },
      { table: 'customer_tags', fk: 'customerId', depth: 1, enforced: false },
      { table: 'wishlist_items', fk: 'customerId', depth: 1, enforced: false },
      { table: 'customer_store_memberships', fk: 'userId', depth: 1, enforced: true, cascades: true },
    ],
    // Any customer who has ordered is reachable from orders.customerId, so in
    // practice MOST customer reverts will be partial. The UI must say this up
    // front rather than letting merchants discover it one record at a time.
    protectedBy: ['orders.customerId'],
  },

  Order: {
    model: 'Order',
    rootTable: 'orders',
    children: [
      { table: 'order_items', fk: 'orderId', depth: 1, enforced: false },
      { table: 'order_status_history', fk: 'orderId', depth: 1, enforced: false },
      { table: 'payments', fk: 'orderId', depth: 1, enforced: false },
      { table: 'payment_transactions', fk: 'paymentId', via: 'payments', depth: 2, enforced: false },
      { table: 'shipments', fk: 'orderId', depth: 1, enforced: false },
      { table: 'refunds', fk: 'orderId', depth: 1, enforced: false },
      { table: 'refund_items', fk: 'refundId', via: 'refunds', depth: 2, enforced: false },
    ],
    // Present in the catalog so expansion knows to STOP here and so "why was
    // this not reverted?" has a machine-readable answer — not because orders
    // are revertible. Time Travel edits catalog and content, never the ledger.
    protectedBy: ['*'],
    refuseReason:
      'Orders, payments, refunds and invoices are financial records. Time Travel can restore ' +
      'your catalog and content, but it will never rewrite what a customer was charged.',
  },

  Promotion: {
    model: 'Promotion',
    rootTable: 'promotions',
    children: [
      { table: 'promotion_rules', fk: 'promotionId', depth: 1, enforced: false },
      { table: 'discount_codes', fk: 'promotionId', depth: 1, enforced: false },
    ],
    protectedBy: [],
  },

  InventoryLocation: {
    model: 'InventoryLocation',
    rootTable: 'locations',
    children: [
      { table: 'inventory_items', fk: 'locationId', depth: 1, enforced: false },
      { table: 'inventory_levels', fk: 'locationId', depth: 1, enforced: false },
      { table: 'inventory_movements', fk: 'locationId', depth: 1, enforced: false },
    ],
    // A movement tied to a fulfilled order is part of that order's history.
    protectedBy: ['inventory_movements.orderId'],
  },

  StorePage: {
    model: 'StorePage',
    rootTable: 'pages',
    children: [{ table: 'navigation_menus', fk: 'pageId', depth: 1, enforced: false }],
    protectedBy: [],
  },
};

/**
 * Tables deliberately outside Time Travel, with the reason.
 *
 * As important as the catalog itself. Reverting the audit log with the undo
 * feature is exactly the sort of thing that looks clever and destroys a
 * forensic record; reverting an invoice counter reissues invoice numbers that
 * have already been printed on legal documents.
 */
export const NOT_TIME_TRAVELLABLE: Record<string, string> = {
  // ── The record of what happened. Reverting these destroys evidence. ────────
  change_log: 'The undo log itself. Reverting it would destroy the record undo depends on.',
  change_batch: 'Groups change_log rows; reverting it would orphan the audit trail it indexes.',
  interface_log: 'A diagnostic record of requests that were served. Cannot be un-served.',
  activity_log: 'Product analytics of what people actually did. Rewriting it would falsify it.',
  email_logs: 'A record of mail that was genuinely delivered. Cannot be un-sent.',

  // ── Financial and legal documents. ────────────────────────────────────────
  invoices: 'Issued invoices are immutable legal documents by design.',
  invoice_counters: 'Reverting a counter reissues invoice numbers already printed on documents.',

  // ── In-flight commerce. Reverting mid-transaction corrupts live sessions. ──
  carts: 'A shopper is holding this right now; changing it under them loses their basket.',
  cart_items: 'Same as carts — live shopper state, not merchant content.',
  checkout_sessions: 'A checkout in progress, possibly mid-payment with a provider.',
  checkout_line_items: 'The priced snapshot a payment was authorised against.',

  // ── Identity, access and sessions. ────────────────────────────────────────
  users: 'Accounts and credentials. Restoring a removed user would resurrect their access.',
  user_sessions: 'Sessions are ephemeral; restoring one would revive a logged-out session.',
  customer_sessions: 'Ephemeral shopper sessions, for the same reason as user_sessions.',
  store_staff: 'Who may act on a store. Reverting this is a privilege change, not an edit.',
  store_staff_permissions: 'Grants. A silent revert here would restore revoked access.',
  invite_tokens: 'Single-use credentials; reviving a spent token reopens a closed invitation.',

  // ── Customer-authored content the merchant does not own. ──────────────────
  reviews: 'Written by shoppers. A merchant must not be able to revert what a customer said.',
  review_photos: 'Images a shopper uploaded with a review they wrote and still own.',
  addresses: 'Entered by customers for their own deliveries, not merchant catalog content.',

  // ── Configuration, not content. ───────────────────────────────────────────
  store_settings: 'Store configuration. Changing it is a settings action with its own history.',
  theme_settings: 'Presentation configuration, edited through the theme editor.',
  store_branding: 'Branding configuration, edited through its own screen.',
  payment_provider_settings: 'Live payment credentials and mode; reverting could break checkout.',
  email_settings: 'Sending configuration, including credentials.',
  templates: 'Email templates have their own versioning and rollback already.',
  shipping_zones: 'Shipping configuration with its own editor and validation rules.',
  shipping_channels: 'Delivery-channel configuration, edited through the shipping screens.',
  shipping_settings: 'Shipping configuration with its own editor and validation rules.',
  shipping_rates: 'Priced rate bands; a silent revert would change delivery charges.',
  tax_slab_profiles: 'Tax configuration. A silent revert would change what customers are charged.',
  platform_settings: 'Platform configuration, not merchant data.',
  store_domains: 'DNS and certificate state lives outside the database; a revert would desync it.',

  // ── Catalog reference data, deliberately deferred. ────────────────────────
  brands: 'Shared reference data across products; needs its own aggregate before it is safe.',
  product_options: 'Option definitions are shared by variants; reverting one reshapes every variant.',
  locations: 'An aggregate root in its own right (InventoryLocation), not a child to revert.',

  // ── Storage and backup bookkeeping. ───────────────────────────────────────
  media_assets: 'Media has its own versioning and trash; two undo systems would fight.',
  media_backup_runs: 'Backup metadata describing media copies, not merchant content.',
  media_backup_entries: 'Per-object rows of a media backup run, not merchant content.',
  merchant_snapshots: 'Records belonging to this subsystem. Reverting them would falsify backup history.',
  bulk_operation_checkpoints: 'The undo handles themselves; reverting them would break undo.',
  time_travel_operations: 'The record of undos performed. Reverting it would hide that they happened.',
  schema_migrations: 'The migration ledger. Reverting it would falsify what has been applied.',
};

/** Every table any aggregate touches, for the CI drift test. */
export function catalogedTables(): Set<string> {
  const tables = new Set<string>();
  for (const entity of Object.values(TIME_TRAVEL_ENTITIES)) {
    tables.add(entity.rootTable);
    for (const child of entity.children) tables.add(child.table);
  }
  return tables;
}

/**
 * Children in write order.
 *
 * INSERTs run parents-first (depth ascending) so a child never references a row
 * that does not exist yet. DELETEs run children-first (depth descending) so a
 * parent is never removed while something still points at it. Getting this
 * backwards fails loudly on the enforced minority and silently corrupts the
 * unenforced majority, which is the worse half.
 */
export function writeOrder(entity: TimeTravelEntity, operation: 'insert' | 'delete'): TimeTravelChild[] {
  const sorted = [...entity.children].sort((a, b) => a.depth - b.depth);
  return operation === 'insert' ? sorted : sorted.reverse();
}

/**
 * Children that a parent DELETE removes for free.
 *
 * These must be counted into the preview even though the undo will not issue a
 * statement for them: the merchant is approving the total blast radius, not the
 * list of statements we happen to run.
 */
export function cascadingChildren(entity: TimeTravelEntity): TimeTravelChild[] {
  return entity.children.filter((c) => c.cascades);
}

export function entityFor(entityType: string): TimeTravelEntity | undefined {
  return TIME_TRAVEL_ENTITIES[entityType];
}
